import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { useEffect, useState } from "react";
import CloseIconBtn from "../../../../../../.././images/CloseIcon_btn.svg";
import {
  Ingredient,
  MenuItem,
  SelectedIngredient,
} from "../../../../../../../interfaces/Catalogue";
import IngredientItem from "../../../../../Ingredient/IngredientItem/IngredientItem";
import "./IngredientModal.scss";
import appConfig from "../../../../../../../appConfig";
import { getPriceByTypeOrderForComposition } from "../../../../../../../utils/priceUtils";
import { useSelector } from "react-redux";
import { RootState } from "../../../../../../../store/rootState";

interface IngredientModalProps {
  menuItem: MenuItem;
  compositionMaxSelection: number;
  ingredients: Ingredient[];
  updateIngredientSelections: (
    selections: SelectedIngredient[],
    eliminatedIngredients: SelectedIngredient[],
    menuItem: MenuItem
  ) => void;
  ingredientSelections: SelectedIngredient[];
  isOpen: boolean;
  onClose: () => void;
  isCheckedItem: boolean;
}

function IngredientModal(props: IngredientModalProps) {
  const {
    menuItem,
    compositionMaxSelection,
    ingredients,
    updateIngredientSelections,
    ingredientSelections,
    isOpen,
    onClose,
    isCheckedItem,
  } = props;

  const selectedMode = useSelector(
    (state: RootState) => state.app.selectedMode
  );

  const initialSelectedIngredients = ingredients
    ?.filter((ingredient: Ingredient) => ingredient.inclus)
    ?.map((ingredient: Ingredient) => ({
      id: ingredient.id,
      ingredient,
      quantity: 1,
    }));

  const [selectedIngredients, setSelectedIngredients] = useState<
    SelectedIngredient[]
  >(isCheckedItem ? ingredientSelections : initialSelectedIngredients);

  const [eliminatedIngredients, setEliminatedIngredients] = useState<
    SelectedIngredient[]
  >([]);

  const handleClose = () => {
    onClose();
  };

  const handleSubmitIngredient = async () => {
    updateIngredientSelections(
      selectedIngredients,
      eliminatedIngredients,
      menuItem
    );
  };

  const updateCheckedCompositions = (
    ingredientId: number,
    ingredient: Ingredient,
    quantity: number
  ) => {
    if (ingredient.isOutOfStock) return;

    if (ingredient.inclus) {
      const eliminatedIngredient = initialSelectedIngredients.find(
        (item) => item.id === ingredientId
      );
      if (eliminatedIngredient) {
        setEliminatedIngredients((prev) => {
          const existing = prev.find((item) => item.id === ingredientId);
          if (existing) {
            return prev?.filter((item) => item.id !== ingredientId);
          }
          return [...prev, { id: ingredientId, ingredient, quantity }];
        });
      }
    }

    setSelectedIngredients((prev) => {
      const existing = prev.find((item) => item.id === ingredientId);
      if (existing) {
        if (quantity === 0) {
          return prev?.filter((item) => item.id !== ingredientId);
        }
        return prev.map((item) =>
          item.id === ingredientId ? { ...item, quantity } : item
        );
      }
      return [...prev, { id: ingredientId, ingredient, quantity }];
    });
  };

  const totalSelectedNotInclus = selectedIngredients?.reduce(
    (total, item) => (!item.ingredient.inclus ? total + item.quantity : total),
    0
  );

  const handleConfirmSelection = () => {
    handleSubmitIngredient();
    handleClose();
  };

  const btnDisabled =
    menuItem.isIngredientRequired && selectedIngredients.length === 0;

  const isMaxSelectionReached = compositionMaxSelection > 1 &&
    totalSelectedNotInclus >= compositionMaxSelection;

  // Calculate total price of additional ingredients (not included by default)
  const totalAdditionalPrice = selectedIngredients?.reduce((total, item) => {
    if (!item.ingredient.inclus) {
      const priceComposition = getPriceByTypeOrderForComposition(
        selectedMode,
        item.ingredient
      );

      return total + (priceComposition * item.quantity);
    }
    return total;
  }, 0);

  return (
    <Dialog
      className="CompositionModal"
      fullWidth={true}
      maxWidth="xs"
      open={isOpen}
      onClose={handleClose}
    >
      <DialogTitle className="TitleModal">
        <div className="title-content">
          <span>Composition</span>
          {menuItem.isIngredientRequired && (
            <span className="required-badge">Requis</span>
          )}
        </div>
        {compositionMaxSelection > 1 && (
          <div className="selection-info">
            <span className="selection-counter">
              {totalSelectedNotInclus} / {compositionMaxSelection} sélectionné(s)
            </span>
            {isMaxSelectionReached && (
              <span className="max-reached-warning">
                Limite atteinte
              </span>
            )}
          </div>
        )}
      </DialogTitle>
      <img
        className="closeModal"
        onClick={handleClose}
        src={CloseIconBtn}
        alt=""
      />

      <DialogContent>
        {ingredients && (
          <div
            className={
              compositionMaxSelection === 1
                ? "compositions-wrapper compositionsContainer"
                : "compositionsContainer"
            }
          >
            {ingredients.map((ingredient) => (
              <IngredientItem
                key={ingredient.id}
                ingredient={ingredient}
                selectedIngredients={selectedIngredients}
                updateCheckedCompositions={updateCheckedCompositions}
                totalSelectedNotInclus={totalSelectedNotInclus}
                compositionMaxSelection={compositionMaxSelection}
              />
            ))}
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          className={"validate" + (btnDisabled ? " disabled" : "")}
          onClick={handleConfirmSelection}
          disabled={btnDisabled}
        >
          <span className="validate-text">Valider</span>
          {totalAdditionalPrice > 0 && (
            <span className="validate-price">+{totalAdditionalPrice.toFixed(2) + appConfig.currency} </span>
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default IngredientModal;